import { useState, useEffect, useCallback } from 'react'
import { useRemainingTime } from './useRemainingTime'
import { BaseVideo, useOnboarding } from '../../../context/OnboardingContext'
import Logger from '../../../helpers/logger'

interface VideoFlowState {
    currentVideo: BaseVideo | null
    isPrePhase: boolean
    shouldShowTeamsBuilding: boolean
    shouldShowCourseStart: boolean
    loading: boolean
}

export const useVideoFlow = () => {
    const { config, getMandatoryVideos } = useOnboarding()
    const { remainingTime } = useRemainingTime()
    const [state, setState] = useState<VideoFlowState>({
        currentVideo: null,
        isPrePhase: true,
        shouldShowTeamsBuilding: false,
        shouldShowCourseStart: false,
        loading: true,
    })

    // Track if a video has been started (selected for viewing)
    const [activeVideoId, setActiveVideoId] = useState<string | null>(null)
    const [isPlayingVideo, setIsPlayingVideo] = useState(false)

    const checkCourseStart = useCallback(() => {
        if (!config) return true
        const courseStartMs = config.onboardingConfig.courseStartTime * 1000
        const currentTimeMs = Date.now()
        const hasStarted = currentTimeMs >= courseStartMs

        // Logger.debug('VideoFlow', 'Course start check:', {
        //     startTimeUTC: new Date(courseStartMs).toUTCString(),
        //     startTimeLocal: new Date(courseStartMs).toLocaleString(),
        //     currentTimeLocal: new Date(currentTimeMs).toLocaleString(),
        //     hasStarted,
        // })

        return hasStarted
    }, [config])

    const areVideosCompleted = useCallback((videos: BaseVideo[]) => {
        const completed = videos.every(video => video.completed)
        // Logger.debug('VideoFlow', 'Checking video completion:', {
        //     totalVideos: videos.length,
        //     completedCount: videos.filter(v => v.completed).length,
        //     allCompleted: completed,
        // })
        return completed
    }, [])

    const hasTimeForVideo = useCallback(
        (video: BaseVideo): boolean => {
            // If this video is currently active, always allow it
            if (video.id === activeVideoId) {
                Logger.debug('VideoFlow', 'Active video - allowing continuation', {
                    videoId: video.id,
                    isPlaying: isPlayingVideo,
                })
                return true
            }

            if (video.type === 'mandatory') {
                Logger.debug('VideoFlow', 'Mandatory video - always has time', {
                    videoId: video.id,
                })
                return true
            }

            if (!remainingTime) {
                Logger.debug('VideoFlow', 'No remaining time available for video', {
                    videoId: video.id,
                })
                return false
            }

            const hasTime = remainingTime > video.length + 5
            Logger.debug('VideoFlow', 'Checking time for video', {
                videoId: video.id,
                videoLength: video.length,
                remainingTime,
                hasTime,
            })
            return hasTime
        },
        [remainingTime, activeVideoId, isPlayingVideo]
    )

    const getNextVideo = useCallback(() => {
        if (!config || !checkCourseStart()) return { video: null, isPrePhase: false }

        // Add comprehensive logging for active video state
        console.log('Video selection state:', {
            activeVideoId,
            isPlayingVideo,
            remainingTime,
            videoStates: config.dailyContent.onboarding.videos.map(v => ({
                id: v.id,
                type: v.type,
                completed: v.completed,
                length: v.length,
            })),
        })

        // CRITICAL FIX: If there's an active video that's playing, maintain it regardless of other conditions
        if (activeVideoId) {
            const activeVideo = config.dailyContent.onboarding.videos.find(v => v.id === activeVideoId)
            Logger.debug('VideoFlow', 'Active video check:', {
                activeId: activeVideoId,
                found: !!activeVideo,
                currentVideo: state.currentVideo?.id,
                isPlaying: isPlayingVideo,
                completed: activeVideo?.completed,
            })
            if (activeVideo && !activeVideo.completed) {
                console.log('Maintaining active playing video:', {
                    videoId: activeVideoId,
                    videoType: activeVideo.type,
                    isPlaying: isPlayingVideo,
                })
                return {
                    video: activeVideo,
                    isPrePhase: activeVideo.metadata.phase === 'pre',
                }
            }
        }

        Logger.debug('VideoFlow', 'Starting next video selection')

        const allVideos = config.dailyContent.onboarding.videos
        const prePhaseVideos = allVideos.filter(v => v.metadata.phase === 'pre')
        const prePhaseCompleted = areVideosCompleted(prePhaseVideos)

        if (!prePhaseCompleted) {
            const nextPrePhase = prePhaseVideos.find(v => !v.completed)
            if (nextPrePhase) {
                return { video: nextPrePhase, isPrePhase: true }
            }
        }

        const mainPhaseVideos = allVideos.filter(
            v => v.metadata.phase === 'main' && v.metadata.category !== 'icebreaker'
        )
        const nextMandatory = mainPhaseVideos.find(v => !v.completed && v.type === 'mandatory')
        if (nextMandatory) {
            return { video: nextMandatory, isPrePhase: false }
        }

        const nextOptional = mainPhaseVideos.find(v => !v.completed && v.type !== 'mandatory' && hasTimeForVideo(v))
        if (nextOptional) {
            return { video: nextOptional, isPrePhase: false }
        }

        return { video: null, isPrePhase: false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, checkCourseStart, activeVideoId, isPlayingVideo, areVideosCompleted, hasTimeForVideo])

    useEffect(() => {
        if (!config) {
            Logger.debug('VideoFlow', 'No config available yet')
            return
        }

        // CRITICAL: Don't update state if we have an active playing video
        if (isPlayingVideo && activeVideoId) {
            Logger.debug('VideoFlow', 'Skipping state update - video is playing', {
                activeVideoId,
                isPlaying: isPlayingVideo,
            })
            return
        }

        const courseStarted = checkCourseStart()
        if (!courseStarted) {
            Logger.debug('VideoFlow', 'Course has not started yet, showing start screen')
            setState({
                currentVideo: null,
                isPrePhase: true,
                shouldShowTeamsBuilding: false,
                shouldShowCourseStart: true,
                loading: false,
            })
            return
        }

        const { video, isPrePhase } = getNextVideo()

        // Ensure active ID matches current video
        if (video?.id !== activeVideoId) {
            Logger.debug('VideoFlow', 'Synchronizing video IDs:', {
                activeId: activeVideoId,
                newVideoId: video?.id,
            })
            setActiveVideoId(video?.id || null)
        }

        const mandatoryVideosCompleted = areVideosCompleted(getMandatoryVideos())

        // Update activeVideoId when a new video is selected
        if (video && !activeVideoId) {
            setActiveVideoId(video.id)
        }

        setState({
            currentVideo: video,
            isPrePhase,
            shouldShowTeamsBuilding: !video && mandatoryVideosCompleted,
            shouldShowCourseStart: !courseStarted,
            loading: false,
        })
    }, [config, getNextVideo, getMandatoryVideos, areVideosCompleted, checkCourseStart, activeVideoId, isPlayingVideo])

    const videoControls = {
        startPlaying: useCallback(() => {
            Logger.debug('VideoFlow', 'Starting video playback:', {
                currentState: isPlayingVideo,
                activeId: activeVideoId,
                currentVideo: state.currentVideo?.id,
            })
            setIsPlayingVideo(true)
            if (state.currentVideo && !activeVideoId) {
                setActiveVideoId(state.currentVideo.id)
            }
        }, [state.currentVideo, activeVideoId, isPlayingVideo]),

        stopPlaying: useCallback(() => {
            Logger.debug('VideoFlow', 'Stopping video playback:', {
                currentState: isPlayingVideo,
                activeId: activeVideoId,
                currentVideo: state.currentVideo?.id,
            })
            setIsPlayingVideo(false)
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [activeVideoId, isPlayingVideo]),

        completeVideo: useCallback(() => {
            Logger.debug('VideoFlow', 'Completing video:', {
                activeId: activeVideoId,
                currentVideo: state.currentVideo?.id,
            })
            setIsPlayingVideo(false)
            setActiveVideoId(null)
            setState(prev => ({ ...prev, currentVideo: null }))
        }, [activeVideoId, state.currentVideo]),
    }

    return { ...state, videoControls, isPlayingVideo }
}
